import { useEffect, useState } from "react";

import {
    Button,
    Checkbox,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    NumberInput,
    NumberInputField,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { HttpStatusCode } from "axios";
import { Field, FieldProps, Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";

import ArrowIcon from "@/assets/ArrowIcon";
import { DURATION_TOAST } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { menuSchema } from "@/schemas/menuSchema";
import { getAllMenus, getMenuById, postMenu, putMenu } from "@/services/menuManagementService";

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

type MenuFormValues = {
    key: string;
    name: string;
    url: string;
    isParent: boolean;
    parentMenuId: string;
    order: number;
    isPrivileges: boolean;
    isReviewer: boolean;
};

const getErrorMessage = (error: unknown): string | undefined =>
    typeof error === "string" ? error : undefined;

const EditMenuManagement = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {
        isOpen: isOpenConfirm,
        onOpen: onOpenConfirm,
        onClose: onCloseConfirm,
    } = useDisclosure();
    const toast = useToast();
    const name = useAppSelector((state: RootState) => state.account.name);

    const [data, setData] = useState<any>(null);
    const [parentMenuOptions, setParentMenuOptions] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [confirmAction, setConfirmAction] = useState<'save' | 'cancel' | null>(null);
    const [pendingValues, setPendingValues] = useState<MenuFormValues | null>(null);

    const { id } = location.state || {};
    const isEdit = Boolean(id);

    const fetchParentMenus = async () => {
        try {
            const response = await getAllMenus({ search: '', pageNumber: 1, pageSize: 1000 }) as any;
            if (response.status === HttpStatusCode.Ok && response.data) {
                const parents = (response.data as any[]).filter((m: any) => m.isParent);
                setParentMenuOptions(parents);
            }
        } catch {
            // non-critical, ignore
        }
    };

    const fetchData = async () => {
        if (!id) return;
        setIsLoading(true);
        try {
            const response = await getMenuById(id) as any;
            if (response.status === HttpStatusCode.Ok && response.data) {
                setData(response.data);
            } else {
                toast({
                    title: 'Load error',
                    description: response?.message || "Failed to load menu.",
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
                navigate('/settings/menu-management');
            }
        } catch (error) {
            toast({
                title: 'Load error',
                description: "An error occurred while loading the menu.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            navigate('/settings/menu-management');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchParentMenus();
        if (isEdit) {
            fetchData();
        }
    }, [id]);

    const handleSubmit = async (values: MenuFormValues) => {
        setPendingValues(values);
        setConfirmAction('save');
        onOpenConfirm();
    };

    const handleConfirmSave = async (values: MenuFormValues) => {
        try {
            const obj = {
                ...values,
                parentMenuId: values.isParent ? EMPTY_GUID : (values.parentMenuId || EMPTY_GUID),
                ...(isEdit && { id: data.id }),
                modifiedBy: name || "System",
                createdBy: name || "System",
            };

            const response = isEdit
                ? await putMenu(obj) as any
                : await postMenu(obj) as any;

            if (response.status === HttpStatusCode.Ok) {
                toast({
                    title: 'Success',
                    description: isEdit ? "Menu updated successfully." : "Menu created successfully.",
                    status: 'success',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
                setTimeout(() => navigate('/settings/menu-management'), 700);
            } else {
                toast({
                    title: 'Save failed',
                    description: response?.message || "Failed to save menu.",
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: 'Save error',
                description: "An error occurred while saving the menu.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    };

    const handleCancel = () => {
        setConfirmAction('cancel');
        onOpenConfirm();
    };

    const handleConfirmCancel = () => {
        navigate('/settings/menu-management');
    };

    const initialValues: MenuFormValues = data
        ? {
            key: data.key || '',
            name: data.name || '',
            url: data.url || '',
            isParent: data.isParent ?? false,
            parentMenuId: data.parentMenuId || EMPTY_GUID,
            order: data.order ?? 0,
            isPrivileges: data.isPrivileges ?? false,
            isReviewer: data.isReviewer ?? false,
        }
        : {
            key: '',
            name: '',
            url: '',
            isParent: false,
            parentMenuId: EMPTY_GUID,
            order: 0,
            isPrivileges: true,
            isReviewer: false,
        };

    return (
        <div style={{ padding: '20px 15px', maxWidth: '600px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', cursor: 'pointer' }} onClick={() => navigate('/settings/menu-management')}>
                <ArrowIcon style={{ transform: 'rotate(180deg)' }} />
                <span>Back</span>
            </div>

            <h2 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>
                {isEdit ? 'Edit Menu' : 'Add Menu'}
            </h2>

            {isLoading && !data && <div>Loading...</div>}

            {!isLoading && (
                <Formik<MenuFormValues>
                    initialValues={initialValues}
                    validationSchema={menuSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {({ errors, touched, isSubmitting, values, setFieldValue }) => (
                        <Form>
                            <div style={{ marginBottom: '15px' }}>
                                <Field name="name">
                                    {({ field }: FieldProps) => (
                                        <FormControl isInvalid={!!(errors.name && touched.name)}>
                                            <FormLabel>Name *</FormLabel>
                                            <Input
                                                {...field}
                                                placeholder="e.g., Settings"
                                            />
                                            <FormErrorMessage>{getErrorMessage(errors.name)}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <Field name="key">
                                    {({ field }: FieldProps) => (
                                        <FormControl isInvalid={!!(errors.key && touched.key)}>
                                            <FormLabel>Key *</FormLabel>
                                            <Input
                                                {...field}
                                                placeholder="e.g., settings"
                                            />
                                            <FormErrorMessage>{getErrorMessage(errors.key)}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <Field name="url">
                                    {({ field }: FieldProps) => (
                                        <FormControl isInvalid={!!(errors.url && touched.url)}>
                                            <FormLabel>URL *</FormLabel>
                                            <Input
                                                {...field}
                                                placeholder="e.g., /settings/menu-management"
                                            />
                                            <FormErrorMessage>{getErrorMessage(errors.url)}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <Field name="order">
                                    {({ field }: FieldProps) => (
                                        <FormControl isInvalid={!!(errors.order && touched.order)}>
                                            <FormLabel>Order *</FormLabel>
                                            <NumberInput
                                                value={field.value}
                                                onChange={(val) => setFieldValue('order', Number(val))}
                                                min={0}
                                            >
                                                <NumberInputField placeholder="e.g., 1" />
                                            </NumberInput>
                                            <FormErrorMessage>{getErrorMessage(errors.order as any)}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>
                            </div>

                            <div style={{ marginBottom: '15px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <Field name="isParent">
                                    {({ field }: FieldProps) => (
                                        <FormControl>
                                            <Checkbox
                                                isChecked={field.value}
                                                onChange={(e) => {
                                                    setFieldValue('isParent', e.target.checked);
                                                    if (e.target.checked) {
                                                        setFieldValue('parentMenuId', EMPTY_GUID);
                                                    }
                                                }}
                                            >
                                                Is Parent Menu
                                            </Checkbox>
                                        </FormControl>
                                    )}
                                </Field>

                                <Field name="isPrivileges">
                                    {({ field }: FieldProps) => (
                                        <FormControl>
                                            <Checkbox
                                                isChecked={field.value}
                                                onChange={(e) => setFieldValue('isPrivileges', e.target.checked)}
                                            >
                                                Requires Privileges
                                            </Checkbox>
                                        </FormControl>
                                    )}
                                </Field>

                                <Field name="isReviewer">
                                    {({ field }: FieldProps) => (
                                        <FormControl>
                                            <Checkbox
                                                isChecked={field.value}
                                                onChange={(e) => setFieldValue('isReviewer', e.target.checked)}
                                            >
                                                Reviewer Only
                                            </Checkbox>
                                        </FormControl>
                                    )}
                                </Field>
                            </div>

                            {!values.isParent && (
                                <div style={{ marginBottom: '15px' }}>
                                    <Field name="parentMenuId">
                                        {({ field }: FieldProps) => (
                                            <FormControl>
                                                <FormLabel>Parent Menu</FormLabel>
                                                <select
                                                    {...field}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #ccc',
                                                    }}
                                                >
                                                    <option value={EMPTY_GUID}>-- No Parent --</option>
                                                    {parentMenuOptions.map((m: any) => (
                                                        <option key={m.id} value={m.id}>{m.name}</option>
                                                    ))}
                                                </select>
                                            </FormControl>
                                        )}
                                    </Field>
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '8px' }}>
                                <Button
                                    type="submit"
                                    colorScheme='blue'
                                    isLoading={isSubmitting}
                                >
                                    Save
                                </Button>
                                <Button
                                    onClick={handleCancel}
                                    variant='outline'
                                >
                                    Cancel
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}

            <ModalConfirmation
                isOpen={isOpenConfirm}
                onClose={onCloseConfirm}
                title={confirmAction === 'save' ? 'Confirm Save' : 'Confirm Cancel'}
                confirmationText={confirmAction === 'save'
                    ? 'Are you sure you want to save this menu?'
                    : 'Are you sure you want to cancel? Unsaved changes will be lost.'}
                handleCancel={onCloseConfirm}
                handleConfirm={() => {
                    if (confirmAction === 'save' && pendingValues) {
                        handleConfirmSave(pendingValues);
                    } else {
                        handleConfirmCancel();
                    }
                    onCloseConfirm();
                }}
                confirmButtonText={confirmAction === 'save' ? 'Save' : 'Yes'}
                cancelButtonText={confirmAction === 'save' ? 'Cancel' : 'No'}
            />
        </div>
    );
};

export default EditMenuManagement;
